import React, { useState, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import useOauth from "@utils/customHooks/useOauth";
import useFlags from "@common/hooks/useFlags";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import API from "@API";
import EditProject from "./EditProject/EditProject";
import EditProjectV2 from "./EditProjectV2";
import NewProjectDetails from "./CreateProjectMspa/NewProjectDetails/NewProjectDetails";
import CreateProjectV2 from "./CreateProjectV2";
import ProjectsList from "./ProjectsList/ProjectsList";
import ProjectsListV2 from "./ProjectsListV2/ProjectsListV2";

export const AccountContext = React.createContext({
  accountId: "",
  accountSlug: "",
  authorizationCode: "",
  planDetails: [],
});

function ProjectsMspa({
  account_slug,
  account_id,
  save_to_cloud_storage_flag,
}) {
  const [authorizationCode] = useOauth();
  const {
    releaseReactNav,
    releaseChangeOrders,
    releaseProjectListV2,
    releaseProjectFormV2,
  } = useFlags();
  // The v2 list ships with change orders, or on its own via its own flag.
  const showProjectsListV2 = !!(releaseChangeOrders || releaseProjectListV2);
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const [fullSalesExecsList, setFullSalesExecsList] = useState(null);
  const [fullPresalesEngineersList, setFullPresalesEngineersList] =
    useState(null);
  const [fullClientList, setFullClientList] = useState(null);
  const [fullTeamsList, setFullTeamsList] = useState(null);
  const [fullTeammateList, setFullTeammateList] = useState(null);
  const [fullTagsList, setFullTagsList] = useState(null);
  const [fullRateTableList, setFullRateTableList] = useState(null);
  const [fullLobList, setFullLobList] = useState(null);
  const [planDetails, setPlanDetails] = useState([]);
  const [statusLabels, setStatusLabels] = useState([]);
  const [fullBusinessUnitList, setFullBusinessUnitList] = useState({
    data: [],
    label: "Business Unit",
  });
  const { accessLevel } = usePrivilegeCheck();
  const canCreate = (privilege) =>
    ["manage", "create"].includes(accessLevel(privilege));
  const salesExecutiveCreatePermission = canCreate("settings.sales_executives");
  const blueprintCreatePermission = canCreate("settings.blueprints");
  const projectOverviewPrivilege = accessLevel("projects.overview");
  const requestApprovalPrivilege =
    accessLevel("projects.request_approval") ?? "manage";
  const [fieldLabels, setFieldLabels] = useState({});

  // Option lists feeding only the legacy list's filters. The v2 list loads its own via RTK Query.
  useEffect(() => {
    if (authorizationCode != "" && !showProjectsListV2) {
      //Get Sales Execs
      API.Get(
        `${apiHost}/v2/sales-executives?page[size]=100`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        setFullSalesExecsList(data);
      });

      //Get pre-sales engineers
      API.Get(
        `${apiHost}/v2/presales-engineers?page[size]=100`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        setFullPresalesEngineersList(data);
      });

      // Get clients
      API.Get(
        `${apiHost}/v2/clients?page[size]=1000`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        setFullClientList(data);
      });

      // Get Teams
      API.Get(
        `${apiHost}/${account_slug}/v1/teams?page[size]=100`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        setFullTeamsList(data);
      });

      // Get Teammates
      API.Get(
        `${apiHost}/${account_slug}/v1/users?page[size]=500`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        setFullTeammateList(data);
      });

      // Get Tags
      API.Get(
        `${apiHost}/v2/tags?page[size]=250`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        setFullTagsList(data);
      });

      // Get Rate Tables
      API.Get(
        `${apiHost}/${account_slug}/v1/rate-tables`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        setFullRateTableList(data);
      });

      // Get LOBs
      API.Get(`${apiHost}/v2/line-of-businesses`, authorizationCode).then(
        (response) => {
          const data = response.data.data;
          setFullLobList(data);
        }
      );
    }
  }, [authorizationCode, showProjectsListV2]);

  // When page loads, call API to get Notes and lift it up to the state
  useEffect(() => {
    if (authorizationCode != "") {
      // Get Questionnairs to determine if questionnaires should be shown
      API.Get(
        `${apiHost}/${account_slug}/v1/questionnaires`,
        authorizationCode
      ).then((response) => {
        const qData = response.data.data;
        let questionnaires = [];
        if (qData && qData !== "") {
          questionnaires = qData;
        }
        API.Get(
          `${apiHost}/v2/account?include=business-units`,
          authorizationCode
        ).then((response) => {
          const data = response.data.data;
          if (questionnaires.length === 0) {
            data.attributes["plan-details"].questionnaires = false;
          }

          setPlanDetails(data.attributes["plan-details"]);
          setFieldLabels(data.attributes["field-labels"] || {});
          setFullBusinessUnitList({
            data:
              response.data?.included?.filter(
                (i) => i.type === "business-units"
              ) || [],

            label: data.attributes?.["field-labels"]?.["business_unit"],
          });

          let statusInfo = [
            "building",
            "technical_approval",
            "sales_approval",
            "business_approval",
            "approved",
            "won",
            "lost",
          ].map((val) => {
            return { status: val, label: data.attributes["field-labels"][val] };
          });
          setStatusLabels(statusInfo);
        });
      });
    }
  }, [authorizationCode]);

  const account = {
    id: account_id,
    slug: account_slug,
    authorizationCode,
    planDetails,
    fieldLabels,
  };

  // When inside ProjectLayout (react-nav), use relative paths
  // since we're nested under /projects/*
  // When in legacy mode, use absolute paths
  const paths = releaseReactNav
    ? { list: "", create: "new", edit: ":project_id/edit" }
    : { list: "/", create: "/new", edit: "/:project_id/edit" };

  return (
    <main>
      <AccountContext.Provider value={account}>
        <Routes>
          <Route
            exact
            path={paths.list}
            element={
              showProjectsListV2 ? (
                <ProjectsListV2 />
              ) : (
                <ProjectsList
                  account_slug={account_slug}
                  account_id={account_id}
                  fullSalesExecsList={fullSalesExecsList}
                  fullPresalesEngineersList={fullPresalesEngineersList}
                  fullClientList={fullClientList}
                  fullTeamsList={fullTeamsList}
                  fullTeammateList={fullTeammateList}
                  fullTagsList={fullTagsList}
                  fullRateTableList={fullRateTableList}
                  fullLobList={fullLobList}
                  fullBusinessUnitList={fullBusinessUnitList}
                  statusLabels={statusLabels}
                  authorizationCode={authorizationCode}
                />
              )
            }
          />
          <Route
            exact
            path={paths.create}
            element={
              releaseProjectFormV2 ? (
                <CreateProjectV2 />
              ) : (
                // Renders the guided flow or NewProject per configGuidedProjectCreation.
                <NewProjectDetails
                  account_id={account_id}
                  account_slug={account_slug}
                  authorizationCode={authorizationCode}
                  save_to_cloud_storage_flag={save_to_cloud_storage_flag}
                  salesExecutiveCreatePermission={salesExecutiveCreatePermission}
                  requestApprovalPrivilege={requestApprovalPrivilege}
                />
              )
            }
          />
          <Route
            path={paths.edit}
            element={
              <div className="projectPageFrame">
                {releaseProjectFormV2 ? (
                  <EditProjectV2 />
                ) : (
                  <EditProject
                    account_slug={account_slug}
                    account_id={account_id}
                    statusLabels={statusLabels}
                    salesExecutiveCreatePermission={
                      salesExecutiveCreatePermission
                    }
                    blueprintCreatePermission={blueprintCreatePermission}
                    projectOverviewPrivilege={projectOverviewPrivilege}
                    requestApprovalPrivilege={requestApprovalPrivilege}
                  />
                )}
              </div>
            }
          />
        </Routes>
      </AccountContext.Provider>
    </main>
  );
}

export default ProjectsMspa;
